import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, FormControlLabel, IconButton, Slider, Stack } from '@mui/material';
import { useSegmentsSlice, useSimulationItemsSlice } from '../../../store/hooks';
import usePersistentStore, { useLayersBucket } from '../../../store/persistent/persistentStore';
import { syncSessionFromXYZ } from '../../../store/session/uiSessionStore';
import { SegmentsLayerState } from '../../../store/layerState';
import { getGrid } from '../../../../store/LoadingStore';
import { ControllerManager } from '@gui/controller/ControllerManager';
import RangeSlider from '@gui/components/generic/RangeSider';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { formatFrameLabel } from './dataUtils';
import { ChevronSelectorHorizontal } from '@untitledui-pro/icons/line';

const buildAggregateList = (range: [number, number], lastIndex: number, wrap: boolean): number[] => {
    const maxIndex = lastIndex;
    const min = range[0];
    const max = range[1];
    if (!wrap || min === max) {
        return Array.from({ length: max - min + 1 }, (_, i) => min + i);
    }
    const head = Array.from({ length: maxIndex - max + 1 }, (_, i) => max + i);
    const tail = Array.from({ length: min + 1 }, (_, i) => i);
    return head.concat(tail);
};

// Ensures that the aggregate range has at least 2 frames
const ensureMinAggregateWidth = (range: [number, number], lastIndex: number): [number, number] => {
    if (lastIndex < 1) return range;
    const min = Math.min(range[0], lastIndex - 1);
    const max = Math.max(range[1], min + 1);
    return [min, Math.min(max, lastIndex)];
};

const rangesEqual = (a: [number, number], b: [number, number]) => a[0] === b[0] && a[1] === b[1];
const arraysEqual = (a: number[], b: number[]) => a.length === b.length && a.every((value, index) => value === b[index]);

const SegmentsSection: React.FC = () => {
    const { segments } = useSegmentsSlice();
    const { simulationItems } = useSimulationItemsSlice();
    const setSegmentsPanel = usePersistentStore((s) => s.setSegmentsPanel);
    const activeLayerID = useLayersBucket((s) => s.activeLayerID);
    const [aggRange, setAggRange] = useState<[number, number]>([0, 0]);
    const aggregateWrap = segments?.aggregateWrap ?? false;
    const goTimer = useRef<ReturnType<typeof setInterval> | null>(null);
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);
    const pipelineRafRef = useRef<number | null>(null);

    const framesInfo = useMemo(() => {
        const layer = getGrid()?.getActiveLayer?.();
        const simKind = layer?.getSimKind();
        const frames = layer?.getFrameCollection?.() ?? [];
        const names = frames.map((f: any, i: number) => f?.name ?? `Frame ${i + 1}`);
        return { names, max: frames.length, simKind };
    }, [activeLayerID]);

    useEffect(() => {
        if (!segments) return;
        const { names, max } = framesInfo;
        if (max === 0) return;

        const hasInvalidFrameSelected = (segments.frameSelected ?? 0) < 0 || (segments.frameSelected ?? 0) > Math.max(0, max - 1);
        const shouldSyncFrameMeta =
            (segments.frameMax ?? 0) !== max || (segments.frameNames?.length ?? 0) !== max || hasInvalidFrameSelected;

        if (shouldSyncFrameMeta) {
            const clampedSel = Math.min(segments.frameSelected ?? 0, Math.max(0, max - 1));
            const validList =
                (segments.framesList?.filter((i) => i >= 0 && i < max) ?? []).length > 0
                    ? segments.framesList!.filter((i) => i >= 0 && i < max)
                    : [0];

            setSegmentsPanel({
                frameMax: max,
                frameNames: names,
                frameSelected: clampedSel,
                framesList: validList,
            });
        }

        const maxIndex = max - 1;
        const fallbackRange = (() => {
            const list = segments.framesList ?? [];
            if (list.length === 0) return [0, maxIndex] as [number, number];
            const sorted = [...list].sort((a, b) => a - b);
            return [sorted[0], sorted[sorted.length - 1]] as [number, number];
        })();
        const nextRange = segments.aggregateRange ?? fallbackRange;

        if (!rangesEqual(aggRange, nextRange)) setAggRange(nextRange);
        if (!segments.aggregateRange || !rangesEqual(segments.aggregateRange, nextRange)) {
            setSegmentsPanel({ aggregateRange: nextRange });
        }

        if (segments.isAggregate) {
            const nextList = buildAggregateList(nextRange, maxIndex, aggregateWrap);
            const currentList = segments.framesList ?? [];
            if (!arraysEqual(currentList, nextList)) {
                setSegmentsPanel({ framesList: nextList });
            }
        }
    }, [framesInfo.max, segments, setSegmentsPanel, aggRange]);

    useEffect(() => {
        syncSessionFromXYZ();
    }, [simulationItems?.x, simulationItems?.y, simulationItems?.z, activeLayerID]);

    const handleSegmentsChange = useCallback(
        <K extends keyof SegmentsLayerState>(key: K, value: SegmentsLayerState[K]) => {
            if (!segments) return;
            setSegmentsPanel({ [key]: value } as Partial<SegmentsLayerState>);
        },
        [segments, setSegmentsPanel]
    );

    const schedulePipelineRun = useCallback(
        (forceAggregate: boolean) => {
            if (pipelineRafRef.current != null) return;
            pipelineRafRef.current = requestAnimationFrame(() => {
                pipelineRafRef.current = null;
                const layer = getGrid().getActiveLayer();
                if (forceAggregate) {
                    layer.activateAggregator();
                }
                pipelineController.clearCacheAll();
                pipelineController.run();
            });
        },
        [pipelineController]
    );

    const handleToggleAggregate = useCallback(
        (checked: boolean) => {
            handleSegmentsChange('isAggregate', checked);

            if (checked) {
                if (segments?.isGo) {
                    handleSegmentsChange('isGo', false);
                    if (goTimer.current) {
                        clearInterval(goTimer.current);
                        goTimer.current = null;
                    }
                }

                const maxIndex = (segments?.frameMax ?? 1) - 1;
                let nextRange = segments?.aggregateRange ?? [0, maxIndex];
                nextRange = ensureMinAggregateWidth(nextRange, maxIndex);
                const wrap = aggregateWrap;
                setAggRange(nextRange);
                handleSegmentsChange('aggregateRange', nextRange);
                handleSegmentsChange('aggregateWrap', wrap);

                const list = buildAggregateList(nextRange, maxIndex, wrap);
                handleSegmentsChange('framesList', list);
                getGrid()?.getActiveLayer()?.activateAggregator();
            }
            schedulePipelineRun(checked);
        },
        [handleSegmentsChange, segments, schedulePipelineRun]
    );

    const handleToggleAggregateWrap = useCallback(() => {
        if (!segments?.isAggregate) return;
        const nextWrap = !aggregateWrap;
        handleSegmentsChange('aggregateWrap', nextWrap);
        const maxIndex = (segments.frameMax ?? 1) - 1;
        const list = buildAggregateList(aggRange, maxIndex, nextWrap);
        handleSegmentsChange('framesList', list);
        getGrid()?.getActiveLayer()?.activateAggregator();
        schedulePipelineRun(true);
    }, [segments, handleSegmentsChange, aggRange, aggregateWrap, schedulePipelineRun]);

    const handleGoToggle = useCallback(
        (checked: boolean) => {
            if (!segments) return;

            handleSegmentsChange('isGo', checked);

            if (goTimer.current) {
                clearInterval(goTimer.current);
                goTimer.current = null;
            }
            if (!checked) return;

            goTimer.current = setInterval(() => {
                const s = usePersistentStore.getState().getActiveLayerState().segments;
                const max = s.frameMax ?? 1;
                const next = (s.frameSelected + 1) % Math.max(1, max);

                usePersistentStore.getState().setSegmentsPanel({ frameSelected: next });
                schedulePipelineRun(false);
            }, 1000);
        },
        [handleSegmentsChange, schedulePipelineRun, segments]
    );

    useEffect(
        () => () => {
            if (goTimer.current) {
                clearInterval(goTimer.current);
                goTimer.current = null;
            }
            if (pipelineRafRef.current != null) {
                cancelAnimationFrame(pipelineRafRef.current);
                pipelineRafRef.current = null;
            }
        },
        []
    );
    const frameCount = Math.max(framesInfo.max ?? 0, segments?.frameMax ?? 0);
    if (frameCount <= 1 || !segments) return null;

    return (
        <Box className="segments-section">
            {!segments.isAggregate && (
                <Stack>
                    <Stack direction="row" alignItems="center" className="segments-frame-row">
                        <Slider
                            className="panel-slider"
                            valueLabelDisplay={'on'}
                            valueLabelFormat={(v) => formatFrameLabel(v as number, framesInfo, true)}
                            size="small"
                            min={0}
                            max={(segments.frameMax ?? 1) - 1}
                            step={1}
                            value={segments.frameSelected ?? 0}
                            onChange={(_, value) => {
                                const idx = Array.isArray(value) ? value[0] : value;
                                setSegmentsPanel({ frameSelected: idx as number });
                                schedulePipelineRun(false);
                            }}
                            sx={{ flex: 1, minWidth: 0 }}
                        />
                    </Stack>
                </Stack>
            )}

            {segments.isAggregate && (
                <Box className="segments-aggregate-row">
                    <Box className="segments-aggregate-slider">
                        <RangeSlider
                            value={aggRange}
                            lastIndex={(segments.frameMax ?? 1) - 1}
                            showValues={true}
                            wrapSelection={aggregateWrap}
                            onChange={([min, max]) => {
                                const nextRange: [number, number] = [min, max];
                                setAggRange(nextRange);
                                handleSegmentsChange('aggregateRange', nextRange);
                                const maxIndex = (segments.frameMax ?? 1) - 1;
                                const list = buildAggregateList(nextRange, maxIndex, aggregateWrap);
                                handleSegmentsChange('framesList', list);
                                getGrid()?.getActiveLayer()?.activateAggregator();
                                schedulePipelineRun(true);
                            }}
                            size="small"
                            valueLabelFormatter={(v) => formatFrameLabel(v, framesInfo, true)}
                            autoDetectPadding
                        />
                    </Box>
                    <CustomTooltip side="left" title="Wrap aggregate range across the end">
                        <IconButton
                            size="small"
                            onClick={handleToggleAggregateWrap}
                            aria-label="Wrap aggregate range across the end"
                            aria-pressed={aggregateWrap}
                            className="segments-aggregate-wrap-button"
                        >
                            <ChevronSelectorHorizontal />
                        </IconButton>
                    </CustomTooltip>
                </Box>
            )}

            <Stack
                className="panel-compact-control-stack"
                direction="row"
                useFlexGap
                flexWrap="wrap"
                alignItems="center"
                sx={{ width: '100%', minWidth: 0 }}
            >
                <FormControlLabel
                    style={{ marginRight: 3 }}
                    control={
                        <Checkbox size="small" checked={!!segments.isAggregate} onChange={(_, checked) => handleToggleAggregate(checked)} />
                    }
                    label="Aggregate"
                />
                {!segments.isAggregate && (
                    <FormControlLabel
                        control={<Checkbox size="small" checked={!!segments.isGo} onChange={(_, checked) => handleGoToggle(checked)} />}
                        label="Go"
                    />
                )}
            </Stack>
        </Box>
    );
};

export default SegmentsSection;
